/**
 * Follow / unfollow mutations with optimistic updates against the shared
 * `keys.follows.followingIds(userId)` cache. Server-side triggers handle
 * notifications, so no extra calls are needed here.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { insertFollow, deleteFollow } from "@/data/raw";
import { keys } from "@/data/keys";
import { trackFollow, trackFollowTap } from "@/lib/analytics";

type Ctx = { previous?: Set<string> };

function followingKey(userId: string) {
  return keys.follows.followingIds(userId);
}

function invalidateFollowScopes(
  qc: ReturnType<typeof useQueryClient>,
  currentUserId: string,
  targetUserId: string,
) {
  qc.invalidateQueries({ queryKey: followingKey(currentUserId) });
  qc.invalidateQueries({ queryKey: keys.follows.following(currentUserId) });
  qc.invalidateQueries({ queryKey: keys.follows.followers(targetUserId) });
}

export function useFollowUser(currentUserId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, string, Ctx>({
    mutationFn: async (targetUserId: string) => {
      if (!currentUserId) throw new Error("Not authenticated");
      const { error } = await insertFollow(currentUserId, targetUserId);
      // Treat duplicate (already following) as success
      if (error && (error as { code?: string }).code !== "23505") throw error;
      if (!error) {
        trackFollow("follow");
        trackFollowTap("follow", targetUserId);
      }
      return { success: true };
    },
    onMutate: async (targetUserId) => {
      if (!currentUserId) return {};
      const key = followingKey(currentUserId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Set<string>>(key);
      qc.setQueryData<Set<string>>(key, (prev) => {
        const next = new Set(prev ?? []);
        next.add(targetUserId);
        return next;
      });
      return { previous };
    },
    onError: (_err, _targetUserId, ctx) => {
      if (!currentUserId || !ctx?.previous) return;
      qc.setQueryData(followingKey(currentUserId), ctx.previous);
    },
    onSettled: (_data, _err, targetUserId) => {
      if (!currentUserId) return;
      invalidateFollowScopes(qc, currentUserId, targetUserId);
    },
  });
}

export function useUnfollowUser(currentUserId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, string, Ctx>({
    mutationFn: async (targetUserId: string) => {
      if (!currentUserId) throw new Error("Not authenticated");
      const { error } = await deleteFollow(currentUserId, targetUserId);
      if (error) throw error;
      trackFollow("unfollow");
      trackFollowTap("unfollow", targetUserId);
      return { success: true };
    },
    onMutate: async (targetUserId) => {
      if (!currentUserId) return {};
      const key = followingKey(currentUserId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Set<string>>(key);
      qc.setQueryData<Set<string>>(key, (prev) => {
        const next = new Set(prev ?? []);
        next.delete(targetUserId);
        return next;
      });
      return { previous };
    },
    onError: (_err, _targetUserId, ctx) => {
      if (!currentUserId || !ctx?.previous) return;
      qc.setQueryData(followingKey(currentUserId), ctx.previous);
    },
    onSettled: (_data, _err, targetUserId) => {
      if (!currentUserId) return;
      invalidateFollowScopes(qc, currentUserId, targetUserId);
    },
  });
}
